// Minimal in-repo renderer for the Perspective design's template DSL.
// It consumes the design's VERBATIM template.html + Component logic and renders
// them with React, so the output is byte-identical to the standalone prototype.
// Only the plumbing here is ours; the markup, CSS and logic are the design's.
//
// Supported subset (all the template actually uses): {{ }} interpolation in
// text and attributes, <sc-if value>, <sc-for list as>, <helmet>, class/style/
// event/ref attribute mapping, and style-hover / style-focus pseudo-styles.
import React from 'react';

const h = React.createElement;

/* ============================ expression resolver ============================
   Mirrors the semantics of the design runtime's expr.ts: dotted/indexed paths,
   ===/!==/==/!= equality, ! negation, and literals. */
const IDENT_RE = /^[A-Za-z_$][A-Za-z0-9_$]*/;
const NUMBER_RE = /^-?\d+(\.\d+)?$/;

export function resolve(vals, src) {
  const expr = String(src).trim();
  if (!expr) return undefined;
  if (expr[0] === '(' && expr[expr.length - 1] === ')' && parensWrapWhole(expr)) {
    return resolve(vals, expr.slice(1, -1));
  }
  const eq = findTopLevelEquality(expr);
  if (eq) {
    const lv = resolve(vals, expr.slice(0, eq.index));
    const rv = resolve(vals, expr.slice(eq.index + eq.op.length));
    switch (eq.op) {
      case '===': return lv === rv;
      case '!==': return lv !== rv;
      case '==': return lv == rv; // eslint-disable-line eqeqeq
      default: return lv != rv;   // eslint-disable-line eqeqeq
    }
  }
  if (expr[0] === '!') return !resolve(vals, expr.slice(1));
  if (expr === 'true') return true;
  if (expr === 'false') return false;
  if (expr === 'null') return null;
  if (expr === 'undefined') return undefined;
  if (NUMBER_RE.test(expr)) return Number(expr);
  if (expr.length >= 2 && (expr[0] === '"' || expr[0] === "'") && expr[expr.length - 1] === expr[0]) {
    return expr.slice(1, -1);
  }
  return resolvePath(vals, expr);
}

function parensWrapWhole(expr) {
  let depth = 0;
  for (let i = 0; i < expr.length - 1; i++) {
    if (expr[i] === '(') depth++;
    else if (expr[i] === ')') { depth--; if (depth === 0) return false; }
  }
  return true;
}

function findTopLevelEquality(expr) {
  let depth = 0;
  for (let i = 0; i < expr.length; i++) {
    const c = expr[i];
    if (c === '[' || c === '(') depth++;
    else if (c === ']' || c === ')') depth--;
    else if (depth === 0 && (c === '=' || c === '!') && expr[i + 1] === '=') {
      if (i > 0 && (expr[i - 1] === '=' || expr[i - 1] === '!')) continue;
      if (!expr.slice(0, i).trim()) continue;
      const op = expr[i + 2] === '=' ? c + '==' : c + '=';
      return { index: i, op };
    }
  }
  return null;
}

function resolvePath(vals, expr) {
  const head = expr.match(IDENT_RE);
  if (!head) return undefined;
  let cur = vals == null ? undefined : vals[head[0]];
  let i = head[0].length;
  while (i < expr.length) {
    if (expr[i] === '.') {
      const m = expr.slice(i + 1).match(IDENT_RE) || expr.slice(i + 1).match(/^\d+/);
      if (!m) return undefined;
      cur = cur == null ? undefined : cur[m[0]];
      i += 1 + m[0].length;
    } else if (expr[i] === '[') {
      let depth = 1, j = i + 1;
      while (j < expr.length && depth > 0) {
        if (expr[j] === '[') depth++;
        else if (expr[j] === ']') { depth--; if (depth === 0) break; }
        j++;
      }
      if (depth !== 0) return undefined;
      const key = resolve(vals, expr.slice(i + 1, j));
      cur = cur == null ? undefined : cur[key];
      i = j + 1;
    } else return undefined;
  }
  return cur;
}

/* ============================ attribute helpers ============================ */
const EVENT_MAP = {
  onclick: 'onClick', onchange: 'onChange', oninput: 'onInput', onsubmit: 'onSubmit',
  onkeydown: 'onKeyDown', onkeyup: 'onKeyUp', onkeypress: 'onKeyPress',
  onmousedown: 'onMouseDown', onmouseup: 'onMouseUp', onmouseenter: 'onMouseEnter',
  onmouseleave: 'onMouseLeave', onmouseover: 'onMouseOver', onmouseout: 'onMouseOut',
  onmousemove: 'onMouseMove', onfocus: 'onFocus', onblur: 'onBlur',
  ondblclick: 'onDoubleClick', oncontextmenu: 'onContextMenu', onwheel: 'onWheel',
  ondragstart: 'onDragStart', ondragend: 'onDragEnd', ondragenter: 'onDragEnter',
  ondragleave: 'onDragLeave', ondragover: 'onDragOver', ondrop: 'onDrop',
  onpointerdown: 'onPointerDown', onpointerup: 'onPointerUp', onpointermove: 'onPointerMove',
};

const kebabToCamel = (s) => s.replace(/-([a-z])/g, (_, c) => c.toUpperCase());

// Hyphenated SVG presentation attributes React wants in camelCase.
const SVG_CAMEL = {
  'stroke-width': 'strokeWidth', 'stroke-linecap': 'strokeLinecap', 'stroke-linejoin': 'strokeLinejoin',
  'stroke-dasharray': 'strokeDasharray', 'stroke-dashoffset': 'strokeDashoffset', 'stroke-miterlimit': 'strokeMiterlimit',
  'stroke-opacity': 'strokeOpacity', 'fill-rule': 'fillRule', 'fill-opacity': 'fillOpacity',
  'clip-rule': 'clipRule', 'clip-path': 'clipPath', 'stop-color': 'stopColor', 'stop-opacity': 'stopOpacity',
  'text-anchor': 'textAnchor',
};

// Camel-cased HTML attributes the HTML parser lowercases (React wants them camel).
const ATTR_CAMEL = {
  readonly: 'readOnly', autofocus: 'autoFocus', tabindex: 'tabIndex', maxlength: 'maxLength',
  minlength: 'minLength', inputmode: 'inputMode', spellcheck: 'spellCheck', autocomplete: 'autoComplete',
  contenteditable: 'contentEditable', crossorigin: 'crossOrigin', novalidate: 'noValidate', srcset: 'srcSet',
};

function cssToObj(css) {
  const o = {};
  for (const decl of String(css).split(';')) {
    const i = decl.indexOf(':');
    if (i < 0) continue;
    const prop = decl.slice(0, i).trim();
    if (!prop) continue;
    o[prop.startsWith('--') ? prop : kebabToCamel(prop)] = decl.slice(i + 1).trim();
  }
  return o;
}

// Returns a function(vals) -> value for an attribute's raw string.
function compileAttr(raw) {
  const whole = raw.match(/^\s*\{\{([\s\S]+?)\}\}\s*$/);
  if (whole) return (vals) => resolve(vals, whole[1]);
  if (raw.includes('{{')) {
    const parts = raw.split(/\{\{([\s\S]+?)\}\}/g);
    return (vals) => parts.map((s, i) => (i & 1 ? resolve(vals, s) ?? '' : s)).join('');
  }
  return () => raw;
}

/* ============================ pseudo-style injection ============================
   style-hover / style-focus values are static in this template, so we mint one
   class per unique (pseudo, css) and inject a rule once. */
let pseudoEl = null;
const pseudoCache = new Map();
function pseudoClass(kind, css) {
  const key = kind + '|' + css;
  const hit = pseudoCache.get(key);
  if (hit) return hit;
  if (!pseudoEl && typeof document !== 'undefined') {
    pseudoEl = document.createElement('style');
    pseudoEl.setAttribute('data-ae-pseudo', '');
    document.head.appendChild(pseudoEl);
  }
  const cls = `ae-${kind}-${pseudoCache.size}`;
  pseudoCache.set(key, cls);
  if (pseudoEl) pseudoEl.textContent += `.${cls}:${kind}{${css}}\n`;
  return cls;
}

/* ============================ template compiler ============================ */
const RAW_WRAP = { select: 'sc-raw-select', table: 'sc-raw-table', thead: 'sc-raw-thead', tbody: 'sc-raw-tbody', tfoot: 'sc-raw-tfoot', tr: 'sc-raw-tr', td: 'sc-raw-td', th: 'sc-raw-th', caption: 'sc-raw-caption' };
const RAW_UNWRAP = Object.fromEntries(Object.entries(RAW_WRAP).map(([k, v]) => [v, k]));

function rawWrap(html) {
  return html.replace(/<(\/?)(select|table|thead|tbody|tfoot|tr|td|th|caption)(?=[\s>])/gi,
    (_, slash, tag) => `<${slash}sc-raw-${tag.toLowerCase()}`);
}

export function compileTemplate(html) {
  const tpl = document.createElement('template');
  tpl.innerHTML = rawWrap(html);
  const builders = walkChildren(tpl.content);
  return (vals) => h(React.Fragment, null, ...builders.map((b, i) => b(vals, i)));
}

function walkChildren(node) {
  return [...node.childNodes].map((c) => walk(c)).filter(Boolean);
}

function walk(node) {
  if (node.nodeType === Node.TEXT_NODE) return walkText(node);
  if (node.nodeType !== Node.ELEMENT_NODE) return null;
  const tag = node.localName.toLowerCase();
  if (tag === 'sc-if') return walkIf(node);
  if (tag === 'sc-for') return walkFor(node);
  if (tag === 'helmet') { injectHelmet(node); return null; }
  return walkElement(node);
}

function walkText(node) {
  const txt = node.nodeValue ?? '';
  if (!txt.includes('{{')) {
    if (!txt.trim() && !txt.includes(' ')) return null; // drop pure indentation
    return () => txt;
  }
  const parts = txt.split(/\{\{([\s\S]+?)\}\}/g);
  return (vals, key) =>
    h(React.Fragment, { key },
      ...parts.map((p, i) => {
        if (!(i & 1)) return p;
        const v = resolve(vals, p);
        if (v === undefined || v === null || typeof v === 'boolean') return null;
        return v; // string, number, element, or array
      }));
}

function walkIf(el) {
  const valGet = compileAttr(el.getAttribute('value') || '');
  const kids = walkChildren(el);
  return (vals, key) =>
    valGet(vals) ? h(React.Fragment, { key }, ...kids.map((b, j) => b(vals, j))) : null;
}

function walkFor(el) {
  const listGet = compileAttr(el.getAttribute('list') || '');
  const asName = el.getAttribute('as') || 'item';
  const kids = walkChildren(el);
  return (vals, key) => {
    const list = listGet(vals);
    if (!Array.isArray(list)) return h(React.Fragment, { key });
    return h(React.Fragment, { key },
      ...list.map((item, i) => {
        const sub = { ...vals, [asName]: item, $index: i };
        return h(React.Fragment, { key: i }, ...kids.map((b, j) => b(sub, j)));
      }));
  };
}

function walkElement(el) {
  const tag = RAW_UNWRAP[el.localName] || el.localName;
  const getters = [];
  const pseudoClasses = [];
  for (const attr of [...el.attributes]) {
    const name = attr.name;
    const value = attr.value;
    if (name.startsWith('hint-') || name === 'data-dc-tpl' || name === 'sc-name') continue;
    if (name === 'style-hover') { pseudoClasses.push(pseudoClass('hover', value)); continue; }
    if (name === 'style-focus') { pseudoClasses.push(pseudoClass('focus', value)); continue; }
    if (name === 'style-active') { pseudoClasses.push(pseudoClass('active', value)); continue; }
    let key = name;
    if (key === 'class') key = 'className';
    else if (key === 'for') key = 'htmlFor';
    else if (key.startsWith('on')) key = EVENT_MAP[key] || 'on' + key[2].toUpperCase() + key.slice(3);
    else if (SVG_CAMEL[key]) key = SVG_CAMEL[key];
    else if (ATTR_CAMEL[key]) key = ATTR_CAMEL[key];
    getters.push([key, compileAttr(value)]);
  }
  const kids = walkChildren(el);
  return (vals, key) => {
    const props = { key };
    for (const [k, get] of getters) {
      let v = get(vals);
      if (k === 'style' && typeof v === 'string') v = cssToObj(v);
      if ((k === 'value' || k === 'checked') && v === undefined) v = k === 'checked' ? false : '';
      props[k] = v;
    }
    if (pseudoClasses.length) {
      props.className = [props.className, ...pseudoClasses].filter(Boolean).join(' ');
    }
    return h(tag, props, ...kids.map((b, j) => b(vals, j)));
  };
}

// <helmet> children (fonts + <style>) are static — inject into <head> once.
let helmetDone = false;
function injectHelmet(el) {
  if (helmetDone || typeof document === 'undefined') return;
  helmetDone = true;
  for (const child of [...el.children]) {
    document.head.appendChild(child.cloneNode(true));
  }
}

/* ============================ logic base + host ============================ */
export class DCLogic {
  constructor(props) {
    this.props = props || {};
    this.state = {};
    this.__host = null;
  }
  setState(update, cb) { this.__host && this.__host.__apply(update, cb); }
  forceUpdate() { this.__host && this.__host.forceUpdate(); }
  componentDidMount() {}
  componentDidUpdate() {}
  componentWillUnmount() {}
  renderVals() { return {}; }
}

// Wraps a design Component class as a real React component.
export function createDesignRoot(templateHtml, ComponentClass, defaultProps = {}) {
  const render = compileTemplate(templateHtml);

  return class DesignRoot extends React.Component {
    constructor(props) {
      super(props);
      this.logic = new ComponentClass({ ...defaultProps, ...props });
      this.logic.__host = this;
      this.state = { tick: 0 };
    }
    __apply(update, cb) {
      const prev = this.logic.state;
      const patch = typeof update === 'function' ? update(prev) : update;
      this.logic.state = { ...prev, ...patch };
      this.setState((s) => ({ tick: s.tick + 1 }), cb);
    }
    componentDidMount() { try { this.logic.componentDidMount(); } catch (e) { console.error(e); } }
    componentDidUpdate(prevProps) { try { this.logic.componentDidUpdate(prevProps); } catch (e) { console.error(e); } }
    componentWillUnmount() { try { this.logic.componentWillUnmount(); } catch (e) { console.error(e); } }
    render() {
      this.logic.props = { ...defaultProps, ...this.props };
      let vals = this.logic.props;
      try {
        vals = { ...this.logic.props, ...(this.logic.renderVals() || {}) };
      } catch (e) {
        console.error('renderVals() failed:', e);
      }
      return render(vals);
    }
  };
}
